/**
 * @fileoverview Contracts for the shared admin DataGrid and its persisted state.
 * @module ui-admin/DataGrid/types
 *
 * Responsibilities:
 * - Define column, row-action, filter, query, and grid-state contracts.
 * Dependencies:
 * - React, lucide-react, and @tanstack/react-table types.
 * Architecture:
 * - Presentation contracts only; persistence behavior is implemented by gridPersistence.
 * Notes:
 * - Contextual visibility defaults are host-derived and never persisted as domain state.
 */
import { ReactNode, SyntheticEvent } from 'react';
import { LucideIcon } from 'lucide-react';
import { CellContext } from '@tanstack/react-table';

// §3.1 Column Definition
export interface DataGridColumn<T> {
  id: string;
  header: string;
  accessorKey?: keyof T & string;
  accessorFn?: (row: T) => any;
  cell?: (info: CellContext<T, unknown>) => ReactNode;
  /**
   * Optional value to use when exporting (CSV / XLSX / PDF). When omitted the
   * exporter falls back to accessorFn / accessorKey and applies a default
   * heuristic for ISO 8601 date strings (renders them as locale date / date+time
   * instead of raw UTC). Use this hook to override that default for columns
   * whose pretty `cell` renderer differs from the raw accessor value.
   */
  exportValue?: (row: T) => string | number | null | undefined;
  /**
   * Optional value the global search box matches against for this column. When
   * omitted, search falls back to accessorFn / accessorKey (the displayed /
   * sorted value). Use this to make a column searchable by fields it does not
   * display or sort by — e.g. a "Case" column that shows the case number but
   * should also match on the related entity name and the raw record id. Keeps
   * search behavior decoupled from sort / display / export.
   */
  searchValue?: (row: T) => string | number | null | undefined;
  sortable?: boolean;
  filterable?: boolean;
  filterOptions?: { value: string; label: string; color?: string }[];
  width?: number | string;
  minWidth?: number;
  maxWidth?: number;
  pinned?: 'left' | 'right';
  hidden?: boolean;
  headerClassName?: string;
  cellClassName?: string;
}

// §3.5 Filter Chips
export interface DataGridFilter {
  id: string;
  label: string;
  type: 'text' | 'select' | 'multi-select' | 'date-range' | 'status';
  options?: { value: string; label: string; color?: string }[];
  defaultValue?: any;
  filterType?: 'text' | 'date' | 'select'; // Type of filter input to show in column filter row
}

// §3.6 Row Selection — Bulk Actions
export interface BulkAction<T> {
  id: string;
  label: string;
  icon?: LucideIcon;
  onClick: (selectedRows: T[]) => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
  tooltip?: string;
}

// §3.8 Row Actions
/**
 * @contract DataGridRowAction<T>
 * Purpose: One host-owned descriptor shared by the grid's row-action menu.
 * Fields: Label/icon/action, optional child actions, visibility/disabled predicates,
 *   variant, and optional expanded-only scope.
 * Rules: Parents with no visible children are omitted; `expandedOnly` descriptors
 *   are omitted from top-level rows and available to host-expanded content.
 * Sync: Presentation/action callback only; host owns persistence.
 */
export interface DataGridRowAction<T> {
  id: string;
  label: string;
  icon?: LucideIcon;
  onClick: (row: T) => void;
  hidden?: (row: T) => boolean;
  disabled?: (row: T) => boolean;
  children?: DataGridRowAction<T>[];
  variant?: 'default' | 'danger';
  expandedOnly?: boolean;
}

/**
 * @contract DataGridExpandedRowActions<T>
 * Purpose: Let host-rendered expanded content open the DataGrid's shared row-action menu for a projected row.
 * Fields: Callback accepts a host row, originating synthetic event, and optional shared row-action descriptors.
 * Rules: Does not render another menu; row actions stay host-owned and menu/focus behavior stays in DataGrid.
 * Sync: Presentation callback only; no data writes.
 */
export interface DataGridExpandedRowActions<T> {
  openRowActions(
    row: T,
    event: SyntheticEvent<HTMLElement>,
    actions?: DataGridRowAction<T>[],
  ): void;
}

/**
 * @contract DataGridRowDragDrop<T>
 * Purpose: Optional host-controlled HTML5 row drag/drop interaction seam.
 * Fields: Row drag eligibility/start/end, optional nested-source activity, nullable target feedback, target drop callback, and optional outside drop zone.
 * Rules: DataGrid only presents drag state; the host owns eligibility and mutations. `isExternalDragActive` refers only to a host-controlled nested member drag, never external browser content. The outside drop zone is shown only when the host says an eligible nested drag is active and receives no arbitrary browser drops.
 * Sync: Presentation callbacks only; persistence and authoritative validation remain in host/domain services.
 */
export interface DataGridRowDragDrop<T> {
  canDrag(row: T): boolean;
  onDragStart(row: T): void;
  onDragEnd?(): void;
  isExternalDragActive?(): boolean;
  getDropState(target: T): { allowed: boolean; reason?: string } | null;
  onDrop(target: T): void;
  outsideDropTarget?: {
    label: string;
    isVisible(): boolean;
    getDropState(): { allowed: boolean; reason?: string } | null;
    onDrop(): void;
  };
}

// §3.2 Server-side fetch contract
export interface FetchParams {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  /** Multi-column sort (includes group-by prepended sorts). Takes precedence over sortBy/sortDirection when present. */
  sorting?: { id: string; desc: boolean }[];
  search?: string;
  filters?: Record<string, any>;
  query?: QueryGroup;
}

export interface FetchResult<T> {
  data: T[];
  total: number;
}

/**
 * @contract DataGridProps
 * Purpose: Configure the shared client/server DataGrid presentation and interactions.
 * Fields: Grid identity, columns, data/fetch callbacks, controls, expanded-row shared actions, optional row drag/drop, and state/context defaults.
 * Rules: Contextual defaults apply only without an explicit contextual visibility override; expanded content reuses the shared action menu and row drag/drop stays host-controlled.
 * Sync: Host supplies local-first rows; grid layout preferences remain browser-local.
 */
export interface DataGridProps<T> {
  gridId: string;

  // Data
  columns: DataGridColumn<T>[];
  data?: T[];
  fetchData?: (params: FetchParams) => Promise<FetchResult<T>>;
  getRowId: (row: T) => string;

  // Toolbar
  searchPlaceholder?: string;
  filters?: DataGridFilter[];
  toolbarActions?: ReactNode;
  toolbarLeftExtra?: ReactNode;
  exportable?: boolean;

  // Selection
  selectable?: boolean;
  bulkActions?: BulkAction<T>[];
  onSelectionChange?: (ids: string[]) => void;

  // Row interaction
  onRowClick?: (row: T) => void;
  rowActions?: DataGridRowAction<T>[];
  /** Optional native row drag/drop seam; DataGrid renders state and forwards targets only. */
  rowDragDrop?: DataGridRowDragDrop<T>;
  showRowChevron?: boolean;
  activeRowId?: string | null;

  // Empty / Loading
  emptyIcon?: LucideIcon;
  emptyTitle?: string;
  emptySubtitle?: string;
  emptyCTA?: { label: string; onClick: () => void };

  // Pagination
  defaultPageSize?: number;
  pageSizeOptions?: number[];

  // Layout
  stickyHeader?: boolean;
  stickyFooter?: boolean;
  containerClassName?: string;

  // Refresh
  refreshKey?: number | string;

  // Column filter types — maps column id to filter input type for the inline filter row
  columnFilterTypes?: Record<string, 'text' | 'select' | 'number' | 'date'>;

  // Expandable rows — render expanded content below a row when clicked
  expandedRowId?: string | null;
  onRowExpand?: (rowId: string | null) => void;
  expandedRowRender?: (row: T, actions: DataGridExpandedRowActions<T>) => React.ReactNode;

  // State persistence — allows parent to restore and track grid state
  initialState?: Partial<GridState>;
  onStateChange?: (state: GridState) => void;
  /** Context-driven column defaults; explicit local/template choices take precedence. */
  contextualColumnDefaults?: Record<string, boolean>;

  // Filtered count callback — called whenever the filtered row count changes
  onFilteredCountChange?: (count: number) => void;

  // Feature tier gating
  isTier2?: boolean;
}

// Query Builder types
export type QueryOperator =
  | 'equals' | 'notEquals'
  | 'contains' | 'notContains' | 'startsWith' | 'endsWith'
  | 'greaterThan' | 'lessThan' | 'greaterThanOrEqual' | 'lessThanOrEqual'
  | 'isEmpty' | 'isNotEmpty'
  | 'between';

export interface QueryCondition {
  id: string;
  field: string;
  operator: QueryOperator;
  value: string;
  value2?: string; // for 'between'
}

export interface QueryGroup {
  id: string;
  logic: 'AND' | 'OR';
  conditions: (QueryCondition | QueryGroup)[];
}

export function isQueryGroup(item: QueryCondition | QueryGroup): item is QueryGroup {
  return 'logic' in item;
}

// Grid state snapshot — everything needed to restore a grid's configuration
export interface GridState {
  columnVisibility: Record<string, boolean>;
  columnOrder: string[];
  sorting: { id: string; desc: boolean }[];
  filterValues: Record<string, any>;
  pageSize: number;
  searchText: string;
  activeQuery?: QueryGroup;
  groupByColumns?: string[];
  /**
   * Per-column filter-row values (TanStack ColumnFiltersState shape). Persisted
   * so a column filter typed before leaving the page is still applied — and
   * visible in the active-filter chips bar — after the grid remounts.
   */
  columnFilters?: { id: string; value: unknown }[];
}

// Grid template — a named, saveable grid state (mirrors MongoDB gridTemplates collection)
export interface GridTemplate {
  _id: string;                    // MongoDB ObjectId as string
  gridType: string;               // "inspections" | "entities" | etc. — which grid this template belongs to
  name: string;
  description?: string;

  // Full grid state snapshot
  state: GridState;

  // Ownership & sharing
  scope: 'personal' | 'shared' | 'system';  // personal = user-only, shared = dept-wide, system = seed/default
  createdBy: {
    userId: string;               // → users._id
    displayName: string;
  };
  updatedBy?: {
    userId: string;
    displayName: string;
  };

  // Usage metadata
  isDefault?: boolean;            // If true, auto-applied when no user preference exists
  isPinned?: boolean;             // User has pinned this template for quick access
  lastUsedAt?: string;            // ISO date
  usageCount: number;

  // Timestamps (ISO strings — would be Date in MongoDB)
  createdAt: string;
  updatedAt: string;

  // Soft delete
  isArchived?: boolean;
}
